import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Box, Typography, Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { setWaPreviewOffset } from '../../../redux/reducers/smartSendWhatsAppSlice';
import WhatsAppPhonePreview from './WhatsAppPhonePreview';

// The WhatsApp card's preview: the mapping screen's phone, in a popup.
// The preview offset is shared in redux with the phone behind this dialog: borrowed (0) on open,
// given back on close/unmount. The phone mounts only after the reset (`ready`), so it never
// fetches the old offset first; `ready` drops only when the exit transition ends.

const WA_PREVIEW_DOM_ID = 'smartsend-wa-summary-preview';

interface Props {
    open: boolean;
    /** The WhatsApp unit's id (WACampaignID). */
    campaignId: number;
    /** The app-wide sheet (whatsappStyles.js). */
    appClasses: any;
    onClose: () => void;
}

const WaPhonePreviewDialog: React.FC<Props> = ({ open, campaignId, appClasses, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);
    const offset: number = useSelector((s: any) => s.smartSendWa.waPreview.offset);
    // Live value for the cleanup (its closure would hold the opening render's offset).
    const offsetNow = useRef(offset);
    offsetNow.current = offset;
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        const restore = offsetNow.current;
        if (restore !== 0) dispatch(setWaPreviewOffset(0));
        setReady(true);
        return () => {
            if (offsetNow.current !== restore) dispatch(setWaPreviewOffset(restore));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            dir={isRTL ? 'rtl' : 'ltr'}
            aria-labelledby="wa-summary-preview-title"
            TransitionProps={{ onExited: () => setReady(false) }}
        >
            <Box style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" id="wa-summary-preview-title">{t('DataSources.send.summary.waPreviewTitle')}</Typography>
            </Box>
            <Box style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: '12px 24px' }}>
                {ready && <WhatsAppPhonePreview campaignId={campaignId} classes={appClasses} previewId={WA_PREVIEW_DOM_ID} />}
            </Box>
            <Box style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={onClose}>{t('DataSources.send.close')}</Button>
            </Box>
        </Dialog>
    );
};

export default WaPhonePreviewDialog;
